'use client';

import { useLoyaltyContext, usePageLayout } from '@libs/common-next';
import { dataLayerPush } from '../gtm/dataLayerPush';
import type { TreatsLoginLocation } from '../gtm/types';
import { useEffect, useRef } from 'react';
import { usePathname } from 'next/navigation';

export const useLoginEvents = () => {
    const { user } = usePageLayout();
    const { memberPoints } = useLoyaltyContext();
    const pathname = usePathname();

    const isLoginAction = useRef<boolean>(false);
    const loginLocation = useRef<TreatsLoginLocation | null>(null);
    const cbFunction = useRef<() => void>(() => {});

    const trackLoginSuccessful = () => {
        dataLayerPush({
            event: 'login-successful',
            login_status: true,
            login_method: loginLocation.current,
            loyalty_id: user?.loyaltyid,
            treats_points: memberPoints?.availablePoints,
            treats_tier: `tier ${String(memberPoints?.currentTierRank)}`,
        });
        cbFunction.current();
    };

    const trackTreatsLogin = (pathOverride: string | null) => {
        dataLayerPush({
            event: 'TreatsLogin',
            loginLocation: loginLocation.current,
            previousPagePath: pathOverride ?? pathname ?? '/',
        });
    };

    const trackSuccessSignIn = (
        location: TreatsLoginLocation | null,
        pathOverride: string | null = null,
        callbackFunction?: () => void
    ) => {
        loginLocation.current = location;
        if (callbackFunction) {
            cbFunction.current = callbackFunction;
        }
        isLoginAction.current = true; // enable useEffect to fire login successful
        trackTreatsLogin(pathOverride);
    };

    useEffect(() => {
        if (
            isLoginAction.current &&
            user?.loyaltyid &&
            memberPoints &&
            loginLocation.current
        ) {
            trackLoginSuccessful();
            isLoginAction.current = false;
        }
    }, [user?.loyaltyid, isLoginAction, memberPoints, loginLocation]);

    return {
        trackSuccessSignIn,
        trackLoginSuccessful,
    };
};
